import React, { useEffect, useState } from "react";
import { Button, Card, Flex, Result, Spin, Typography } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const { Text } = Typography;

/**
 * Resolves an account number to the account it belongs to, then redirects.
 *
 * External systems (PixelShield admin, for one) record the account number a
 * person reads off the CRM screen — "A202600047" — not the UUID the edit URL
 * needs. This gives them a stable link to point at:
 *
 *     /accounts/by-number/A202600047  ->  /accounts/edit/<uuid>
 */
const AccountByNumber = () => {
  const { accountNumber } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      try {
        const response = await axios.get(
          `accounts/account/?account_number=${encodeURIComponent(accountNumber)}`
        );
        if (cancelled) return;
        const match = (response.data?.results || [])[0];
        if (match) {
          // replace: the resolver is a redirect, not a page worth going back to.
          navigate(`/accounts/edit/${match.id}/`, { replace: true });
        } else {
          setError("not_found");
        }
      } catch (err) {
        if (!cancelled) setError("failed");
      }
    };

    if (accountNumber) resolve();
    return () => {
      cancelled = true;
    };
  }, [accountNumber, navigate]);

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Card bordered={false}>
          <Result
            status={error === "not_found" ? "404" : "500"}
            title={
              error === "not_found"
                ? "No account with that number"
                : "Could not look up that account"
            }
            subTitle={
              <>
                <Text code>{accountNumber}</Text>
                {error === "not_found" && (
                  <div style={{ marginTop: 8 }}>
                    Check the number recorded in the other system — it should match
                    the Account Number shown on the account here.
                  </div>
                )}
              </>
            }
            extra={
              <Button type="primary" onClick={() => navigate("/accounts")}>
                Go to Accounts
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <Flex align="center" justify="center" vertical gap="middle" style={{ padding: 80 }}>
      <Spin size="large" />
      <Text type="secondary">
        Opening account <Text code>{accountNumber}</Text>…
      </Text>
    </Flex>
  );
};

export default AccountByNumber;
